import { useMemo } from "react";

import {
  EmptystateModulesColored,
  EmptystateSearchNoResultsColored,
} from "components/icons/generated";
import SearchInput from "components/SearchInput";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import { URL_SEARCH_KEY } from "constants/urlQueryKeys";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useURLParams from "hooks/useURLParams";
import { fuzzySearch } from "utils/fuzzySearch";
import useLocalTableSort from "views/Account/Integrations/registry/shared/hooks/useLocalTableSort";

import ModuleVersionDependenciesSection from "./components/Section";
import sectionStyles from "./components/Section/styles.module.css";
import { FILTERS_ORDER_SETTINGS_KEY_VERSION_DEPENDENCIES, NO_STICKY_RIGHT } from "./constants";
import {
  MODULE_VERSION_MODULE_DEPENDENCIES_TABLE_CELLS,
  MODULE_VERSION_PROVIDER_DEPENDENCIES_TABLE_CELLS,
} from "./Table/Cells";
import {
  INITIAL_SORT_DIRECTION,
  MODULE_DEPENDENCIES_INITIAL_SORT_OPTION,
  MODULE_DEPENDENCIES_SORT_CONFIG,
  MODULE_VERSION_MODULE_DEPENDENCIES_TABLE_FILTERS_STORAGE_KEY,
  MODULE_VERSION_PROVIDER_DEPENDENCIES_TABLE_FILTERS_STORAGE_KEY,
  PROVIDER_DEPENDENCIES_INITIAL_SORT_OPTION,
  PROVIDER_DEPENDENCIES_SORT_CONFIG,
} from "./Table/constants";
import { sortModuleDependencies, sortProviderDependencies } from "./Table/helpers";
import useModuleVersionModuleDependenciesTableStore from "./Table/useModuleTableStore";
import useModuleVersionProviderDependenciesTableStore from "./Table/useProviderTableStore";
import ModuleVersionPageIntoTitle from "../components/PageIntoTitle";
import ModuleVersionScrollableWrapper from "../components/ScrollableWrapper";

type ModuleVersionDependenciesProps = {
  providerDependencies: Array<{
    name: string;
    version: string;
    source: string;
  }>;
  moduleDependencies: string[];
};

const ModuleVersionDependencies = ({
  providerDependencies,
  moduleDependencies,
}: ModuleVersionDependenciesProps) => {
  const urlParams = useURLParams();
  const searchQuery = urlParams.get(URL_SEARCH_KEY);
  const trackAnalytics = useAnalytics();

  const { externalSortManagement: moduleExternalSortManagement, filterParams: moduleFilterParams } =
    useLocalTableSort({
      localStorageKey: MODULE_VERSION_MODULE_DEPENDENCIES_TABLE_FILTERS_STORAGE_KEY,
      sortConfig: MODULE_DEPENDENCIES_SORT_CONFIG,
      initialSortOption: MODULE_DEPENDENCIES_INITIAL_SORT_OPTION,
      initialSortDirection: INITIAL_SORT_DIRECTION,
    });

  const {
    externalSortManagement: providerExternalSortManagement,
    filterParams: providerFilterParams,
  } = useLocalTableSort({
    localStorageKey: MODULE_VERSION_PROVIDER_DEPENDENCIES_TABLE_FILTERS_STORAGE_KEY,
    sortConfig: PROVIDER_DEPENDENCIES_SORT_CONFIG,
    initialSortOption: PROVIDER_DEPENDENCIES_INITIAL_SORT_OPTION,
    initialSortDirection: INITIAL_SORT_DIRECTION,
  });

  const sortedModuleDependencies = useMemo(() => {
    const rows = moduleDependencies.map((dependency) => ({ id: dependency }));

    const filtered = searchQuery
      ? fuzzySearch(rows, searchQuery.trim(), { keys: ["id"], scoreThreshold: -1000 })
      : rows;

    return sortModuleDependencies(filtered, moduleFilterParams);
  }, [moduleDependencies, searchQuery, moduleFilterParams]);

  const sortedProviderDependencies = useMemo(() => {
    const rows = providerDependencies.map((dependency) => ({
      id: dependency.name,
      version: dependency.version,
      source: dependency.source,
    }));

    const filtered = searchQuery
      ? fuzzySearch(rows, searchQuery.trim(), {
          keys: ["id", "source", "version"],
          scoreThreshold: -1000,
        })
      : rows;

    return sortProviderDependencies(filtered, providerFilterParams);
  }, [providerDependencies, searchQuery, providerFilterParams]);

  const isEmpty = sortedModuleDependencies.length === 0 && sortedProviderDependencies.length === 0;

  return (
    <>
      <PageInfoHeaderWrapper>
        <ModuleVersionPageIntoTitle
          title="Dependencies"
          tooltip="Modules and providers this module depends on."
        />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder={SEARCH_PLACEHOLDERS.DEPENDENCIES}
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY_VERSION_DEPENDENCIES}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <ModuleVersionScrollableWrapper gap="x-large">
        {isEmpty && (
          <EmptyState
            padding="x-large"
            icon={searchQuery ? EmptystateSearchNoResultsColored : EmptystateModulesColored}
            caption={
              searchQuery
                ? "No dependencies match your search query."
                : "This module has no dependencies."
            }
            fullHeight
            announce={!!searchQuery}
          />
        )}
        {sortedModuleDependencies.length > 0 && (
          <ModuleVersionDependenciesSection
            title="Module dependencies"
            count={sortedModuleDependencies.length}
          >
            <TableSimple
              data={sortedModuleDependencies}
              store={useModuleVersionModuleDependenciesTableStore}
              wrapperClassName={sectionStyles.tableWrapper}
              ariaLabel="Module dependencies"
              trackAnalytics={trackAnalytics}
              cellPerColumn={MODULE_VERSION_MODULE_DEPENDENCIES_TABLE_CELLS}
              filtersStorageKey={MODULE_VERSION_MODULE_DEPENDENCIES_TABLE_FILTERS_STORAGE_KEY}
              sortConfig={MODULE_DEPENDENCIES_SORT_CONFIG}
              externalSortManagement={moduleExternalSortManagement}
              hasPagination={false}
              hideSettings
              stickyRight={NO_STICKY_RIGHT}
            />
          </ModuleVersionDependenciesSection>
        )}
        {sortedProviderDependencies.length > 0 && (
          <ModuleVersionDependenciesSection
            title="Provider dependencies"
            count={sortedProviderDependencies.length}
          >
            <TableSimple
              data={sortedProviderDependencies}
              store={useModuleVersionProviderDependenciesTableStore}
              wrapperClassName={sectionStyles.tableWrapper}
              ariaLabel="Provider dependencies"
              trackAnalytics={trackAnalytics}
              cellPerColumn={MODULE_VERSION_PROVIDER_DEPENDENCIES_TABLE_CELLS}
              filtersStorageKey={MODULE_VERSION_PROVIDER_DEPENDENCIES_TABLE_FILTERS_STORAGE_KEY}
              sortConfig={PROVIDER_DEPENDENCIES_SORT_CONFIG}
              externalSortManagement={providerExternalSortManagement}
              hasPagination={false}
            />
          </ModuleVersionDependenciesSection>
        )}
      </ModuleVersionScrollableWrapper>
    </>
  );
};

export default ModuleVersionDependencies;
